/**
 * "Assign Sales to Chatter" — de dialoog achter de knop op Analytics.
 *
 * De bundle laat hier de verkopen uit de periode zien met de chatter die er
 * nu aan hangt, en laat je die met de hand overschrijven. Een toewijzing is
 * POST /chatter-finance/sale-override; "unassigned" is sinds EARN-004 ook een
 * POST (met kind), zodat de automatische regel hem niet terugzet.
 */
import { useState } from 'react'
import { Modal } from './Modal'
import { useActions, mut } from '../lib/actions'
import { useApi } from '../lib/useApi'
import { Table, Loading, ErrorNote } from './ui'
import { eur, dt } from '../lib/format'

interface Sale {
  id: number
  timestamp: string | null
  platform: string | null
  model: string | null
  fan: string | null
  gross: number | null
  net: number | null
  chatter_id: number | null
  chatter: string | null
  is_override: boolean
}
interface Chatter { id: number; name: string }

export function AssignSalesDialog({
  days,
  platform,
  onClose,
}: {
  days: string
  platform: string
  onClose: () => void
}) {
  const { run, busy } = useActions()
  const [pitchedOnly, setPitchedOnly] = useState(false)
  const sales = useApi<{ data: Sale[] } | Sale[]>(
    `/api/admin/chatter-finance/sales-list?days=${days}&platform=${platform}`,
  )
  const chatters = useApi<Chatter[]>('/api/admin/models/chatters')

  const raw = Array.isArray(sales.data) ? sales.data : sales.data?.data ?? []
  const rows = pitchedOnly ? raw.filter((r) => r.chatter_id != null) : raw

  // 14-09-2026 (EARN-004): "unassigned" is een expliciete keuze die de
  // automatische regel overstemt (POST met kind), geen DELETE meer — een DELETE
  // zette de regel terug en die koos dan dezelfde chatter opnieuw. Het veld
  // heet revenue_event_id; `sale_id` gaf een 422.
  const assign = (sale: Sale, chatterId: string) =>
    run({
      label: `sale-${sale.id}`,
      request: chatterId
        ? mut.post('/api/admin/chatter-finance/sale-override', {
            revenue_event_id: sale.id, chatter_id: Number(chatterId),
          })
        : mut.post('/api/admin/chatter-finance/sale-override', {
            revenue_event_id: sale.id, chatter_id: null, kind: 'unassigned',
          }),
      onDone: () => sales.refetch(),
      successText: chatterId ? 'Sale assigned.' : 'Assignment released.',
    })

  return (
    <Modal
      title="Assign Sales to Chatter"
      desc="A manual assignment overrides the automatic attribution. Leaving it empty releases the sale again."
      width={900}
      onClose={onClose}
    >
      <div className="row" style={{ marginBottom: 10 }}>
        <label className="chkrow">
          <span className={pitchedOnly ? 'chk on' : 'chk'}
                onClick={() => setPitchedOnly((v) => !v)}>
            {pitchedOnly ? '✓' : ''}
          </span>
          Only sales already assigned
        </label>
      </div>

      {sales.loading && !sales.data ? <Loading /> : sales.error ? (
        <ErrorNote error={sales.error} />
      ) : (
        <Table
          cols={[
            { key: 't', label: 'Time' }, { key: 'p', label: 'Platform' },
            { key: 'm', label: 'Model' }, { key: 'f', label: 'Fan' },
            { key: 'g', label: 'Gross', num: true }, { key: 'n', label: 'Net', num: true },
            { key: 'c', label: 'Chatter' },
          ]}
          emptyText="No sales in this period."
        >
          {rows.map((r) => (
            <tr key={r.id}>
              <td className="mut2" style={{ fontSize: 12 }}>{dt(r.timestamp)}</td>
              <td className="mut">{r.platform ?? '—'}</td>
              <td className="mut">{r.model ?? '—'}</td>
              <td>{r.fan ?? '—'}</td>
              <td className="num mono">{eur(r.gross)}</td>
              <td className="num mono">{eur(r.net)}</td>
              <td>
                <select
                  className="inp sm"
                  disabled={busy === `sale-${r.id}`}
                  value={r.chatter_id ?? ''}
                  onChange={(e) => assign(r, e.target.value)}
                >
                  <option value="">— unassigned —</option>
                  {(chatters.data ?? []).map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </td>
            </tr>
          ))}
        </Table>
      )}
    </Modal>
  )
}
